import React, { useState, useContext, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ConfigProvider, theme, Switch, Layout, Row, Col, Menu, Popover } from 'antd';
import {
  TeamOutlined,
  TrophyOutlined,
  DashboardOutlined,
  OrderedListOutlined,
  ReadOutlined,
  MoonOutlined,
  SunOutlined,
  MenuOutlined
} from '@ant-design/icons';
import ajaxMe from './ajax/me';
import UserInfoMenu from './components/UserInfoMenu';
import * as Settings from './utils/AuthUtils';
import { MeContext } from './context/';
import PublicRoutes from './routing/PublicRoutes';
import PrivateRoutes from './routing/PrivateRoutes';
import LangSelection from './components/LangSelection';
import logo from './assets/LPI_LOGO_WHITE.png';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useTranslation } from 'react-i18next';


const { Header, Content, Footer } = Layout;
const { defaultAlgorithm, darkAlgorithm } = theme;

const basepath = process.env.PUBLIC_URL
  ? `${process.env.PUBLIC_URL}`
  : '/'; 

function App() {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [hasRights, setHasRights] = useState({isAdmin: false, isReferral: false});
  const [width, setWidth] = useState(window.innerWidth);
  const me = useContext(MeContext);
  const { t } = useTranslation();
  const location = useLocation();
  const storedPrevUrl = localStorage.getItem('prevUrl');
  const storedMode = localStorage.getItem('darkMode');

  function handleWindowSizeChange() {
    setWidth(window.innerWidth);
  }

  useEffect(() => {
    const isAd = (me && me.payLoad ? me.payLoad.isAdmin : false)
    const isRef = Settings.isReferral(me && me.payLoad ? me.payLoad : {}); 
    setHasRights({isAdmin: isAd, isReferral: isRef});
    if (storedPrevUrl && storedPrevUrl !== '' && storedPrevUrl != null) {
      const actualUrl = location.pathname;
      if (storedPrevUrl === actualUrl) {
        localStorage.setItem('prevUrl', null);
      }
    }
    if (storedMode && storedMode !== '' && storedMode != null) {
      setIsDarkMode((storedMode.toLowerCase() === 'true' ? true : false));
    }
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
        window.removeEventListener('resize', handleWindowSizeChange);
    }
  }, [me, isDarkMode])

  function generateMenu(isMobile,isLogged) {
    const menuMode = (isMobile ? 'vertical' : 'horizontal');
    return (<Menu
              theme="dark"
              mode={menuMode}
              defaultSelectedKeys={['2']}
            >
              {isLogged && 
                <Menu.Item key="/dashboard">
                  <Link to={`${basepath}dashboard`} className="no-link" viewTransition>
                    <DashboardOutlined /> {t('menu.dashboard')}
                  </Link>
                </Menu.Item>
              }
              <Menu.Item key="/leagues">
                <Link to={`${basepath}find-league`} className="no-link" viewTransition>
                    <TrophyOutlined /> {t('menu.leagues')}
                </Link>
              </Menu.Item>
              <Menu.Item key="/seasons">
                <Link to={`${basepath}seasons`} className="no-link" viewTransition>
                    <TrophyOutlined /> {t('menu.seasons')}
                </Link>
              </Menu.Item>
              <Menu.Item key="/tournaments">
                <Link to={`${basepath}tournaments`} className="no-link" viewTransition>
                    <TeamOutlined /> {t('menu.tournaments')}
                </Link>
              </Menu.Item>
              {/* <Menu.SubMenu title="Tournaments" icon={<FontAwesomeIcon icon={faCalendarDays} />}>
                <Menu.Item key="tournaments/archive">
                  <Link to={`${basepath}tournaments`} className="no-link" viewTransition>
                      <TeamOutlined /> Archive
                  </Link>
                </Menu.Item>
              </Menu.SubMenu> */}
              <Menu.Item key="/ranking">
                <Link to={`${basepath}rankings`} className="no-link" viewTransition>
                  <OrderedListOutlined /> {t('menu.lpiranking')}
                </Link>
              </Menu.Item>
              <Menu.Item key="/rulebook">
                <Link to={`${basepath}rulebook`} className="no-link" viewTransition>
                  <ReadOutlined /> {t('menu.lpirulebook')}
                </Link>
              </Menu.Item>
              <Menu.Item key="/user" className="user-menu-item">
                <UserInfoMenu />
              </Menu.Item>
            </Menu>)
  }

  function generateHeader(isMobile,isLogged) {
    if (isMobile) {
      return (
        <Header>
          <Popover style={{ color: '#fff', padding: '0px' }} content={generateMenu(isMobile,isLogged)}><MenuOutlined className='nav-phone-icon'/></Popover>
          <Row justify="center">
            <Col span={7}>
              <div className="logo">
                <Link to={basepath} viewTransition>
                  <img src={logo} alt="lpi logo" height="48px" style={{ marginTop: '8px', marginRight: '16px', float: 'left' }}/> 
                </Link>
              </div>      
            </Col>
          </Row>
        </Header>
      )
    }
    return (
      <Header>
        <div className="logo">
          <Link to={basepath} viewTransition>
            <img src={logo} alt="lpi logo" height="48px" style={{ marginTop: '8px', marginRight: '16px', float: 'left' }}/> 
          </Link>
        </div>
        {generateMenu(isMobile,isLogged)}
      </Header>
    );
  }

  function onChangeMode() {
    if (isDarkMode) {
      localStorage.setItem('darkMode', false);
    } else {
      localStorage.setItem('darkMode', true);
    }
    setIsDarkMode((previousValue) => !previousValue);
  }

  function toggleIconText() {
    if (isDarkMode) {
      return(<span style={{ marginRight: '8px' }}><SunOutlined/> Light Theme </span>);
    } 
    return (<span style={{ marginRight: '8px' }}><MoonOutlined/> Dark Theme </span>);
  }

  const isMobile = width <= 768;

  if (me && me.payLoad && me.payLoad.name !== '') {
    return (
      <ConfigProvider theme={{algorithm: isDarkMode ? darkAlgorithm : defaultAlgorithm, }}>
      <div className="App">
        <Layout className="layout" style={{minHeight:"100vh"}}>
          <div style={{ width: '100%', textAlign: 'right', padding: '3px', paddingRight: '20px', backgroundColor: '#1b1c1dff' }}>
            <div className="uppermenu-item">
              {toggleIconText()}
              <Switch checked={isDarkMode} onChange={onChangeMode}/>
            </div>
            <LangSelection />
          </div>
          {generateHeader(isMobile,true)}
          <Content>
            { /* <NavBreadcrumb /> */}
            <div className="site-layout-content">
              <PrivateRoutes basepath={basepath} hasRights={hasRights} />
            </div>
          </Content>
          <Footer style={{ textAlign: 'center' }}>
            Pauper League Manager ©2022 Created and maintained by <b>Paolo Iuculano</b><br/>
            It contains unofficial Fan Content permitted under the Fan Content Policy. Not approved/endorsed by Wizards. Portions of the materials used are property of Wizards of the Coast. <b>©Wizards of the Coast LLC.</b>
          </Footer>
        </Layout>
      </div>
      </ConfigProvider>
    );
  } else {
    // console.warn('atterro qua in app.js nella parte non autenticata!');
    return (
      <ConfigProvider theme={{algorithm: isDarkMode ? darkAlgorithm : defaultAlgorithm, }}>
      <div className="App">
        <Layout className="layout" style={{minHeight:"100vh"}}>
          <div style={{ width: '100%', textAlign: 'right', padding: '3px', paddingRight: '20px', backgroundColor: '#080809' }}>
            <div className="uppermenu-item">
              <span style={{ marginRight: '8px' }}><MoonOutlined/> Dark Theme </span>
              <Switch onChange={onChangeMode}/>
            </div>
            <LangSelection />
          </div>
          {generateHeader(isMobile,false)}
          <Content>
            { /* <NavBreadcrumb /> */ }
            <div className="site-layout-content">
              <PublicRoutes basepath={basepath} hasRights={hasRights} />
            </div>
          </Content>
          <Footer style={{ textAlign: 'center' }}>
            Pauper League Manager ©2022 Created and maintained by <b>Paolo Iuculano</b><br/>
            It contains unofficial Fan Content permitted under the Fan Content Policy. Not approved/endorsed by Wizards. Portions of the materials used are property of Wizards of the Coast. <b>©Wizards of the Coast LLC.</b>
          </Footer>
        </Layout>
      </div>
      </ConfigProvider>
    )
  }
}

export default ajaxMe(App);

